// CompositionCanvas: the client DOM renderer for a PostComposition. A faithful
// mirror of the worker's render-composition.ts buildCompositionHtml, so what the
// user sees == the published master (parity guarded by composition.parity.spec).
//
// The stage is rendered at the composition's NATIVE pixel size and uniformly scaled
// to fit the container (transform: scale), so every layer keeps the exact px geometry
// the worker uses — no per-layer rounding drift. Pure render: no editing here; the
// editor overlays selection/handles on top using the same geometry + scale.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import './fonts.css'
import type {
  DecorLayer,
  ImageLayer,
  ImageSrc,
  Layer,
  LogoLayer,
  PostComposition,
  TextLayer,
  TextRun,
} from './types'
import { decorUri, encircleBox, encircleUri, isDecorMark } from './decor'
import {
  fillToCss,
  formatRunText,
  groupRunsIntoLines,
  pxBox,
  resolveFontFamily,
} from './render-helpers'
import { useAttachmentUrl } from './use-attachment-url'
import { useCompositionGoogleFonts } from './use-composition-fonts'

function srcUrl(src: ImageSrc): string | undefined {
  if (src.kind === 'url') return src.url
  if (src.kind === 'dataUri') return src.dataUri
  // `media` is resolved to url server-side; `attachment` resolves via useAttachmentUrl.
  return undefined
}

function boxStyle(layer: Layer, dims: { w: number; h: number }): CSSProperties {
  const b = pxBox(layer, dims)
  return {
    position: 'absolute',
    left: b.left,
    top: b.top,
    width: b.width,
    height: b.height,
    transform: b.rotation ? `rotate(${b.rotation}deg)` : undefined,
    opacity: layer.opacity != null && layer.opacity < 1 ? layer.opacity : undefined,
  }
}

function TextLayerView({
  layer,
  dims,
  reportOverflow,
}: {
  layer: TextLayer
  dims: { w: number; h: number }
  reportOverflow?: (id: string, overflowing: boolean) => void
}) {
  const dir = layer.direction === 'rtl' ? 'rtl' : 'ltr'
  const align =
    layer.align === 'center' ? 'center' : layer.align === 'right' ? 'flex-end' : 'flex-start'
  const ls = layer.letterSpacingPct
    ? layer.letterSpacingPct * layer.fontSizePct * dims.h
    : undefined

  // Measure (layout-neutral) whether this text clips the canvas frame: the content's bottom
  // (top + actual content height) past the stage means the published master — which clips to
  // the canvas — would cut it off. A ResizeObserver catches font/size/wrap changes. Only runs
  // when the editor supplies reportOverflow (never on read-only previews / worker parity).
  const rootRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    if (!reportOverflow) return
    const el = rootRef.current
    if (!el) return
    const measure = () => {
      const top = layer.y * dims.h
      const contentBottom = top + el.scrollHeight
      // 2px slack absorbs sub-pixel rounding so a perfectly-fitting headline never flickers.
      reportOverflow(layer.id, contentBottom > dims.h + 2)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    // Only stop observing on unmount — do NOT clear the layer's overflow here. Unmount also
    // happens when the layer is suppressed for inline editing; the CompositionCanvas aggregator
    // prunes truly-removed layers by id, so the warning persists while you edit and clears only
    // when the layer is actually gone.
    return () => ro.disconnect()
  }, [reportOverflow, layer.id, layer.y, dims.h])

  // Render one run as its styled block. A single-run line renders exactly this node (identical
  // to the prior per-run layout); inline runs are the same nodes inside a wrapping row.
  // Typography treatments (italic / underline / highlight / encircle) mirror the worker's
  // render-composition.ts runOf exactly, so the canvas shows what the master renders.
  const runNode = (run: TextRun, key: string) => {
    const { text, forceLtr } = formatRunText(run.text, layer.uppercase)
    const fam = resolveFontFamily(run.fontFamily ?? layer.fontFamily, run.text)
    const size = (run.fontSizePct ?? layer.fontSizePct) * dims.h
    const runStyle: CSSProperties = {
      display: 'flex',
      direction: forceLtr ? 'ltr' : dir,
      textAlign: layer.align,
      fontFamily: `"${fam}"`,
      fontWeight: run.fontWeight ?? layer.fontWeight,
      fontSize: size,
      lineHeight: layer.lineHeight,
      letterSpacing: ls,
      color: run.color ?? layer.color,
      whiteSpace: 'pre-wrap',
      // Never synthesize a faux italic/bold the satori master can't render (satori
      // does no synthesis): a face without a real italic stays upright on BOTH sides.
      fontSynthesis: 'none',
      fontStyle: run.italic ? 'italic' : undefined,
      borderBottom: run.underlineColor
        ? `${Math.max(4, Math.round(size * 0.07))}px solid ${run.underlineColor}`
        : undefined,
      background: run.highlightColor || undefined,
      padding: run.highlightColor
        ? `${Math.round(size * 0.02)}px ${Math.round(size * 0.08)}px`
        : undefined,
    }
    if (!run.encircleColor) {
      return (
        <div key={key} style={runStyle}>
          {text}
        </div>
      )
    }
    // The hand-drawn loop stretched around the run (box math shared with both worker
    // renderers via decor.ts::encircleBox).
    const b = encircleBox(run.text.length, size)
    return (
      <div key={key} style={{ display: 'flex', position: 'relative' }}>
        <div style={runStyle}>{text}</div>
        <img
          src={encircleUri(run.encircleColor, b.w, b.h)}
          alt=""
          width={b.w}
          height={b.h}
          draggable={false}
          style={{ position: 'absolute', top: b.top, left: b.left }}
        />
      </div>
    )
  }

  // Runs group into lines (a run flows inline only when run.inline). A one-run line keeps the
  // exact prior markup (parity); a multi-run line is a wrapping row of inline runs sharing a
  // baseline-ish bottom edge. See groupRunsIntoLines (shared with the worker master).
  return (
    <div
      ref={rootRef}
      style={{
        ...boxStyle(layer, dims),
        display: 'flex',
        flexDirection: 'column',
        alignItems: align,
        justifyContent: 'flex-start',
        direction: dir,
      }}
    >
      {groupRunsIntoLines(layer.runs).map((line, li) =>
        line.length === 1 ? (
          runNode(line[0]!, String(li))
        ) : (
          <div
            key={li}
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: align,
              alignItems: 'flex-end',
              direction: dir,
            }}
          >
            {line.map((run, ri) => runNode(run, `${li}-${ri}`))}
          </div>
        )
      )}
    </div>
  )
}

function ImageLayerView({
  layer,
  dims,
}: {
  layer: ImageLayer | LogoLayer
  dims: { w: number; h: number }
}) {
  const attachmentId = layer.src.kind === 'attachment' ? layer.src.attachmentId : undefined
  const resolvedAttachment = useAttachmentUrl(attachmentId)
  const url = srcUrl(layer.src) ?? resolvedAttachment
  const fit = layer.type === 'logo' ? 'contain' : layer.objectFit
  const focal =
    layer.type === 'image'
      ? `${(layer.focalX ?? 0.5) * 100}% ${(layer.focalY ?? 0.5) * 100}%`
      : undefined
  const radius =
    layer.type === 'image' && layer.radiusPct
      ? Math.min(pxBox(layer, dims).width, pxBox(layer, dims).height) * layer.radiusPct
      : undefined
  return (
    <div style={{ ...boxStyle(layer, dims), overflow: 'hidden', borderRadius: radius }}>
      {url ? (
        <img
          src={url}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: fit, objectPosition: focal }}
          draggable={false}
        />
      ) : null}
    </div>
  )
}

function LayerView({
  layer,
  dims,
  reportOverflow,
}: {
  layer: Layer
  dims: { w: number; h: number }
  reportOverflow?: (id: string, overflowing: boolean) => void
}): ReactNode {
  if (layer.hidden) return null
  switch (layer.type) {
    case 'image':
    case 'logo':
      return <ImageLayerView layer={layer} dims={dims} />
    case 'overlay':
      return <div style={{ ...boxStyle(layer, dims), background: fillToCss(layer.fill) }} />
    case 'shape': {
      const b = pxBox(layer, dims)
      let borderRadius: number | string | undefined
      if (layer.shapeKind === 'pill') borderRadius = 9999
      else if (layer.shapeKind === 'ellipse') borderRadius = '50%'
      else if (layer.radiusPct) borderRadius = Math.min(b.width, b.height) * layer.radiusPct
      return (
        <div
          style={{ ...boxStyle(layer, dims), background: fillToCss(layer.fill), borderRadius }}
        />
      )
    }
    case 'decor':
      return <DecorLayerView layer={layer} dims={dims} />
    case 'text':
      return <TextLayerView layer={layer} dims={dims} reportOverflow={reportOverflow} />
  }
}

// A hand-made accent mark, regenerated from mark + color at the layer's box size
// (worker decorHtml mirror — declarative, recolor/resize stays crisp).
function DecorLayerView({ layer, dims }: { layer: DecorLayer; dims: { w: number; h: number } }) {
  if (!isDecorMark(layer.mark)) return null
  const b = pxBox(layer, dims)
  return (
    <div style={{ ...boxStyle(layer, dims), display: 'flex' }}>
      <img
        src={decorUri(layer.mark, layer.color)}
        alt=""
        width={Math.round(b.width)}
        height={Math.round(b.height)}
        draggable={false}
        style={{ width: b.width, height: b.height }}
      />
    </div>
  )
}

// Live playback of TIMED text (video overlays): the provider's rAF clock re-renders every frame,
// so entrance/exit presets are computed per-frame as plain styles (no CSS keyframes to keep in
// sync with mount timing). Mirrors the container's burned-in fade/slide/pop closely enough for
// an editing preview; the rendered mp4 stays pixel-final.
// MUST stay in lockstep with the container's TEXT_ANIM_SEC (apps/socialkit/container/
// render-server.mjs) — neither side can import the other.
const TEXT_ANIM_SEC = 0.3

function easeOutBack(p: number): number {
  const c = 1.70158
  const q = p - 1
  return 1 + (c + 1) * q * q * q + c * q * q
}

function timedTextStyle(
  layer: Layer,
  t: number,
  dims: { w: number; h: number }
): CSSProperties | 'hidden' | undefined {
  // ANY layer type can carry a show window in video overlays (L1).
  if (!layer.timing) return undefined
  const { start, end } = layer.timing
  if (t < start - 1e-6 || t >= end) return 'hidden'
  const pIn = layer.anim?.in ? Math.min(1, (t - start) / TEXT_ANIM_SEC) : 1
  const pOut = layer.anim?.out ? Math.min(1, (end - t) / TEXT_ANIM_SEC) : 1
  if (pIn >= 1 && pOut >= 1) return undefined
  let opacity = 1
  const transforms: string[] = []
  const applyPreset = (preset: 'fade' | 'slide' | 'pop', p: number, dirY: number) => {
    opacity = Math.min(opacity, p)
    if (preset === 'slide') transforms.push(`translateY(${(1 - p) * dirY * 0.05 * dims.h}px)`)
    if (preset === 'pop') transforms.push(`scale(${0.8 + 0.2 * easeOutBack(p)})`)
  }
  if (layer.anim?.in && pIn < 1) applyPreset(layer.anim.in, pIn, 1)
  if (layer.anim?.out && pOut < 1) applyPreset(layer.anim.out, pOut, -1)
  const b = pxBox(layer, dims)
  return {
    position: 'absolute',
    inset: 0,
    opacity,
    transform: transforms.length ? transforms.join(' ') : undefined,
    transformOrigin: `${b.left + b.width / 2}px ${b.top + b.height / 2}px`,
  }
}

export type CompositionCanvasProps = {
  composition: PostComposition
  className?: string
  /** Fixed display width in px; when omitted the canvas fills its container width. */
  displayWidth?: number
  /** Fit-to-contain: cap the stage height so the WHOLE composition fits a viewport box
   *  (scale = min(width/dims.w, maxHeight/dims.h)). The wrapper then sizes to the stage so the
   *  parent can center it; overlay coords stay anchored to the stage's top-left. */
  maxHeight?: number
  /** Notifies the parent (editor) of the current scale + display size for overlays. */
  onScaleChange?: (info: { scale: number; displayWidth: number; displayHeight: number }) => void
  /** Extra content rendered INSIDE the scaled stage (e.g. editor selection handles). */
  children?: ReactNode
  /** Editor-only: skip rendering this layer (it's being inline-edited via an overlay). */
  suppressLayerId?: string
  /** Editor-only: notified with the ids of text layers whose content clips the canvas frame
   *  (the published master clips to the canvas, so these would be cut off). Omitted on
   *  read-only previews — the measurement only runs when a handler is supplied. */
  onTextOverflow?: (overflowingLayerIds: string[]) => void
  /** Video playback only: the beat-local time in seconds. Timed text layers show only inside
   *  their window and animate their fade/slide/pop presets. Absent (all non-playback uses) =
   *  every layer renders — paused editing always shows all text. */
  beatTime?: number
  /** VIDEO beat overlays are transparent-canvas BY DESIGN (they composite over the beat's
   *  motion background) — without this the no-background fallback paints opaque #0B0B0C and
   *  blacks out the clip behind the text. Static/carousel keep the opaque fallback (master
   *  parity: a static post must never see through to the page). */
  transparent?: boolean
  style?: CSSProperties
}

export function CompositionCanvas({
  composition,
  className,
  displayWidth,
  maxHeight,
  onScaleChange,
  children,
  suppressLayerId,
  onTextOverflow,
  beatTime,
  transparent,
  style,
}: CompositionCanvasProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const dims = { w: composition.canvas.width, h: composition.canvas.height }
  const [measured, setMeasured] = useState<number | undefined>(displayWidth)

  // Real brand/curated families load on demand (Google Fonts, registry-mirrored
  // ladder); the vendored logical faces come from the bundled fonts.css.
  useCompositionGoogleFonts(composition)

  // Aggregate per-text-layer overflow into one set, reported up on change. Each TextLayerView
  // measures its own rendered content (layout-neutral) and calls reportOverflow.
  const overflowRef = useRef<Map<string, boolean>>(new Map())
  const reportOverflow = useCallback(
    (id: string, overflowing: boolean) => {
      if (!onTextOverflow) return
      const cur = overflowRef.current.get(id)
      if (cur === overflowing) return
      overflowRef.current.set(id, overflowing)
      onTextOverflow([...overflowRef.current].filter(([, v]) => v).map(([k]) => k))
    },
    [onTextOverflow]
  )

  // Prune overflow entries for layers no longer in the composition (deleted/hidden) so the Map
  // can't grow stale and a removed layer stops warning. We prune by the FULL layer list (not the
  // rendered `ordered`), so a layer that's only momentarily un-rendered — e.g. suppressed during
  // inline text editing — KEEPS its last-measured overflow (the warning persists while you fix
  // it, instead of flickering off the instant you double-click to edit).
  const overflowKey = onTextOverflow ? composition.layers.map(l => l.id).join(',') : ''
  useEffect(() => {
    if (!onTextOverflow) return
    const valid = new Set(composition.layers.map(l => l.id))
    let changed = false
    for (const id of [...overflowRef.current.keys()]) {
      if (!valid.has(id)) {
        overflowRef.current.delete(id)
        changed = true
      }
    }
    if (changed) onTextOverflow([...overflowRef.current].filter(([, v]) => v).map(([k]) => k))
    // overflowKey is the stable signal for "the set of layer ids changed".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overflowKey, onTextOverflow])

  // Clear the shared overflow set when this canvas unmounts (navigating away / swapping the
  // composer to another creative), so a stale warning can't bleed onto the next post — worker
  // layer ids (ly_hook_1, …) are NOT globally unique across creatives.
  useEffect(() => {
    if (!onTextOverflow) return
    const overflow = overflowRef.current
    return () => {
      overflow.clear()
      onTextOverflow([])
    }
  }, [onTextOverflow])

  useLayoutEffect(() => {
    if (displayWidth) {
      setMeasured(displayWidth)
      return
    }
    const el = wrapRef.current
    if (!el) return
    const update = () => setMeasured(el.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [displayWidth])

  const w = measured ?? dims.w
  // Fit-to-contain when maxHeight is set: the smaller of width-fit and height-fit so the whole
  // stage is visible. Otherwise width-fit as before.
  const scale = maxHeight != null ? Math.min(w / dims.w, maxHeight / dims.h) : w / dims.w
  const displayWidthPx = dims.w * scale
  const displayHeight = dims.h * scale

  useLayoutEffect(() => {
    onScaleChange?.({ scale, displayWidth: displayWidthPx, displayHeight })
  }, [scale, displayWidthPx, displayHeight, onScaleChange])

  const ordered = [...composition.layers]
    .filter(l => l.id !== suppressLayerId)
    .sort((a, b) => a.z - b.z)
  const bg = composition.canvas.background
    ? fillToCss(composition.canvas.background)
    : transparent
      ? 'transparent'
      : '#0B0B0C'

  return (
    <div
      ref={wrapRef}
      className={className}
      style={{
        position: 'relative',
        // Fit-to-contain sizes the wrapper to the stage (so the parent centers it); otherwise
        // fill the container width as before.
        width: maxHeight != null ? displayWidthPx : (displayWidth ?? '100%'),
        height: displayHeight,
        ...style,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: dims.w,
          height: dims.h,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          overflow: 'hidden',
          background: bg,
        }}
      >
        {ordered.map(layer => {
          const timed = beatTime != null ? timedTextStyle(layer, beatTime, dims) : undefined
          if (timed === 'hidden') return null
          const node = (
            <LayerView
              key={layer.id}
              layer={layer}
              dims={dims}
              reportOverflow={onTextOverflow ? reportOverflow : undefined}
            />
          )
          // A TIMED layer keeps its wrapper for the whole playback pass, even when no ramp is
          // active (timed undefined): flipping div↔bare at the anim edges changes the element
          // type under the same key, remounting the subtree (visible img re-decode flash at
          // exactly t=start+ANIM and again when the exit ramp begins).
          return beatTime != null && layer.timing ? (
            <div key={layer.id} style={timed}>
              {node}
            </div>
          ) : (
            node
          )
        })}
      </div>
      {children}
    </div>
  )
}
